// src/app/hours/services/hours.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map, of, switchMap } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  CreateBookedHoursRequest,
  CurrentUserDto,
  DayApprovalStatus,
  DraftCell,
  IsoDate,
  PagedResponse,
  UserWonDto,
  WeeklyReportBookedHourItem,
  WeeklyReportResponse,
  YywwDto,
} from '../models/hours.model';

@Injectable({ providedIn: 'root' })
export class HoursService {
  private readonly baseUrl = environment.apiBaseUrl;
  private readonly MAX_PAGES = 200;

  constructor(private http: HttpClient) {}

  /* =============================
     Core API Calls
     ============================= */

  getCurrentUser(): Observable<CurrentUserDto> {
    return this.http.get<CurrentUserDto>(`${this.baseUrl}/api/account/get-current-user`);
  }

  getWeeklyReport(fullYearWeek: string): Observable<WeeklyReportResponse> {
    const params = new HttpParams().set('FullYearWeek', fullYearWeek);

    return this.http
      .get<WeeklyReportResponse>(`${this.baseUrl}/api/user-transactions/user-weekly-clock-logs`, {
        params,
      })
      .pipe(map((response) => this.normalizeWeeklyReportResponse(response)));
  }

  getAllUserWons(employeeId: number, pageSize = 50): Observable<UserWonDto[]> {
    return this.getAllPages<UserWonDto>((requestedPage) => {
      const params = this.buildPagingParams(requestedPage, pageSize).set(
        'employeeId',
        String(employeeId),
      );

      return this.http.get<PagedResponse<UserWonDto>>(`${this.baseUrl}/api/user-wons`, { params });
    });
  }

  getAllWeeksForYear(year: number, pageSize = 50): Observable<YywwDto[]> {
    return this.getAllPages<YywwDto>((requestedPage) => {
      const params = this.buildPagingParams(requestedPage, pageSize).set(
        'searchTerm',
        String(year),
      );

      return this.http.get<PagedResponse<YywwDto>>(`${this.baseUrl}/api/yywws`, { params });
    });
  }

  createBookedHours(req: CreateBookedHoursRequest): Observable<unknown> {
    const safe: CreateBookedHoursRequest = {
      ...req,
      hours: this.clampNonNegative(req.hours),
    };

    return this.http.post(`${this.baseUrl}/api/booked-hours`, safe);
  }

  patchBookedHoursTotal(bookedHoursId: number, totalHours: number): Observable<unknown> {
    const safeTotal = this.clampNonNegative(totalHours);

    return this.http.patch(
      `${this.baseUrl}/api/booked-hours/${bookedHoursId}/hours/${encodeURIComponent(
        String(safeTotal),
      )}`,
      null,
    );
  }

  /* =============================
     Weekly Report Normalization
     ============================= */

  private normalizeWeeklyReportResponse(response: WeeklyReportResponse): WeeklyReportResponse {
    const emptyResponse: WeeklyReportResponse = {
      status: response?.status ?? 'error',
      data: {
        payableHoursSync: null,
        timeLogs: [],
        bookedhours: [],
        userSchedules: [],
        invalidUserClockLogs: [],
        dayOffRequests: [],
      },
    };

    if (response?.status !== 'success' || !response.data) {
      return emptyResponse;
    }

    return {
      status: response.status,
      data: {
        payableHoursSync: response.data.payableHoursSync ?? null,
        timeLogs: response.data.timeLogs ?? [],
        bookedhours: (response.data.bookedhours ?? []).map((wrapper) => {
          const user = wrapper.user;

          return {
            ...wrapper,
            user: {
              ...user,
              bookedHours: (user.bookedHours ?? []).map((day) => {
                const entries = day.bookedHours ?? day.bookedHour ?? [];

                return {
                  date: this.toDateKey(day.date),
                  bookedHours: entries,
                  bookedHour: entries,
                };
              }),
            },
            totalHours:
              typeof wrapper.totalHours === 'number'
                ? this.round2(wrapper.totalHours)
                : this.sumBookedHoursForUserWrapper(wrapper),
          };
        }),
        userSchedules: response.data.userSchedules ?? [],
        invalidUserClockLogs: response.data.invalidUserClockLogs ?? [],
        dayOffRequests: response.data.dayOffRequests ?? [],
      },
    };
  }

  /* =============================
     Weekly Report Helpers
     ============================= */

  isPayrollSynced(report: WeeklyReportResponse | null | undefined): boolean {
    return !!report?.data?.payableHoursSync?.id;
  }

  getClockedHoursForDate(report: WeeklyReportResponse, employeeId: number, date: IsoDate): number {
    const user = report.data.timeLogs.find((row) => row.user.id === employeeId);
    const day = user?.user.logs?.find((logDay) => this.toDateKey(logDay.date) === date);

    const total = (day?.timeLogs ?? []).reduce(
      (sum, timeLog) => sum + (Number(timeLog.totalHours ?? timeLog.hours) || 0),
      0,
    );

    return this.round2(total);
  }

  getBookedHoursForDate(report: WeeklyReportResponse, employeeId: number, date: IsoDate): number {
    const total = this.getBookedHourItemsForDate(report, employeeId, date).reduce(
      (sum, bookedHour) => sum + (Number(bookedHour.hours) || 0),
      0,
    );

    return this.round2(total);
  }

  getBookedHourItemsForDate(
    report: WeeklyReportResponse,
    employeeId: number,
    date: IsoDate,
  ): WeeklyReportBookedHourItem[] {
    const wrap = report.data.bookedhours?.find((row) => row.user?.id === employeeId);
    const day = wrap?.user?.bookedHours?.find((bookedDay) => this.toDateKey(bookedDay.date) === date);

    return this.getBookedEntriesForDay(day);
  }

  getDayApprovalStatus(
    report: WeeklyReportResponse,
    employeeId: number,
    date: IsoDate,
  ): DayApprovalStatus {
    const items = this.getBookedHourItemsForDate(report, employeeId, date).filter(
      (item) => (Number(item.hours) || 0) > 0 || !!item.id,
    );

    if (!items.length) {
      return 'none';
    }

    const approvedCount = items.filter((item) => this.isBookedHourSupervisorApproved(item)).length;

    if (approvedCount === 0) {
      return 'none';
    }

    return approvedCount === items.length ? 'approved' : 'partial';
  }

  isDayFullyApproved(report: WeeklyReportResponse, employeeId: number, date: IsoDate): boolean {
    return this.getDayApprovalStatus(report, employeeId, date) === 'approved';
  }

  isBookedHourSupervisorApproved(item: WeeklyReportBookedHourItem | null | undefined): boolean {
    return !!item?.supervisorApprovedDate || !!item?.approvedBySupervisor;
  }

  getBookedHourRowForWonAndDate(
    report: WeeklyReportResponse,
    employeeId: number,
    workOrderNumberId: number,
    date: IsoDate,
  ): { id: number; hours: number; isSupervisorApproved: boolean } | null {
    const row = this.getBookedHourItemsForDate(report, employeeId, date).find(
      (bookedHour) => bookedHour.workOrderNumber?.id === workOrderNumberId,
    );

    if (!row) {
      return null;
    }

    return {
      id: row.id,
      hours: this.round2(Number(row.hours) || 0),
      isSupervisorApproved: this.isBookedHourSupervisorApproved(row),
    };
  }

  getWeeklyBookedTotalForWon(
    report: WeeklyReportResponse,
    employeeId: number,
    workOrderNumberId: number,
  ): number {
    const wrap = report.data.bookedhours?.find((row) => row.user?.id === employeeId);

    const total =
      (wrap?.user?.bookedHours ?? []).reduce((sum, day) => {
        const dayTotal = this.getBookedEntriesForDay(day)
          .filter((bookedHour) => bookedHour.workOrderNumber?.id === workOrderNumberId)
          .reduce((daySum, bookedHour) => daySum + (Number(bookedHour.hours) || 0), 0);

        return sum + dayTotal;
      }, 0) ?? 0;

    return this.round2(total);
  }

  matchesClocked(booked: number, clocked: number): boolean {
    return Math.abs(booked - clocked) < 0.01;
  }

  /* =============================
     Draft Helpers
     ============================= */

  saveDraftCell(employeeId: number, cell: DraftCell): Observable<'noop' | 'created' | 'patched'> {
    const hours = this.clampNonNegative(cell.hours);

    if (cell.bookedHourId != null) {
      return this.patchBookedHoursTotal(cell.bookedHourId, hours).pipe(
        map(() => 'patched' as const),
      );
    }

    if (hours <= 0) {
      return of('noop' as const);
    }

    const payload: CreateBookedHoursRequest = {
      employeeId,
      hours,
      bookedDate: cell.date,
      workOrderNumberId: cell.workOrderNumberId,
    };

    return this.createBookedHours(payload).pipe(map(() => 'created' as const));
  }

  /* =============================
     Internals
     ============================= */

  private getBookedEntriesForDay(
    day:
      | {
          bookedHours?: WeeklyReportBookedHourItem[] | null;
          bookedHour?: WeeklyReportBookedHourItem[] | null;
        }
      | null
      | undefined,
  ): WeeklyReportBookedHourItem[] {
    return day?.bookedHours ?? day?.bookedHour ?? [];
  }

  private sumBookedHoursForUserWrapper(wrapper: {
    user?: {
      bookedHours?: Array<{
        bookedHours?: WeeklyReportBookedHourItem[] | null;
        bookedHour?: WeeklyReportBookedHourItem[] | null;
      }> | null;
      totalHours?: number | null;
    } | null;
    totalHours?: number | null;
  }): number {
    const directTotal = Number(wrapper.totalHours ?? wrapper.user?.totalHours);

    if (Number.isFinite(directTotal) && directTotal > 0) {
      return this.round2(directTotal);
    }

    const total =
      (wrapper.user?.bookedHours ?? []).reduce((sum, day) => {
        const dayTotal = this.getBookedEntriesForDay(day).reduce(
          (daySum, bookedHour) => daySum + (Number(bookedHour.hours) || 0),
          0,
        );

        return sum + dayTotal;
      }, 0) ?? 0;

    return this.round2(total);
  }

  private getAllPages<T>(
    fetchPage: (requestedPage: number) => Observable<PagedResponse<T>>,
  ): Observable<T[]> {
    const loop = (requestedPage: number, acc: T[], lastSignature?: string): Observable<T[]> => {
      if (requestedPage > this.MAX_PAGES) {
        throw new Error(
          `Pagination exceeded MAX_PAGES=${this.MAX_PAGES}. Backend paging params likely incorrect.`,
        );
      }

      return fetchPage(requestedPage).pipe(
        switchMap((res) => {
          const data = res?.data ?? [];
          const nextAcc = acc.concat(data);

          const currentPage = Number(res?.currentPage ?? requestedPage);
          const totalPages = Number(res?.totalPages ?? currentPage);

          const head = data.slice(0, 3);
          const signature = `${currentPage}/${totalPages}:${data.length}:` + JSON.stringify(head);

          if (lastSignature && signature === lastSignature && totalPages > 1) {
            throw new Error(
              `Backend pagination did not advance. ` +
                `Requested page=${requestedPage}, response currentPage=${currentPage}, totalPages=${totalPages}. ` +
                `Likely wrong query param for page.`,
            );
          }

          const serverSaysDone = currentPage >= totalPages;
          const emptyMeansDone = data.length === 0;

          if (serverSaysDone || emptyMeansDone) {
            return of(nextAcc);
          }

          return loop(requestedPage + 1, nextAcc, signature);
        }),
      );
    };

    return loop(1, []);
  }

  private buildPagingParams(requestedPage: number, pageSize: number): HttpParams {
    return new HttpParams()
      .set('pageSize', String(pageSize))
      .set('page', String(requestedPage))
      .set('pageNumber', String(requestedPage))
      .set('pageIndex', String(requestedPage))
      .set('currentPage', String(requestedPage));
  }

  private toDateKey(value: string | null | undefined): string {
    return String(value ?? '').slice(0, 10);
  }

  private clampNonNegative(value: number): number {
    const safeValue = Number.isFinite(value) ? value : 0;

    return safeValue < 0 ? 0 : this.round2(safeValue);
  }

  private round2(value: number): number {
    return Math.round((Number.isFinite(value) ? value : 0) * 100) / 100;
  }
}